import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { TYPE_CREATOR } from "../helpers/types";
import useDecodedToken from "../hooks/userHooks/useDecodedToken";
import useFetchOtherUserInfos from "../hooks/userHooks/useFetchOtherUserInfos";
import { StoreState } from "../redux/store";
import { authService } from "../services";
import { trackingLinksService } from "../services/tracking-links.service";

const removeTllFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete("tll");
  const newUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState({}, "", newUrl);
};

const TrackLinkTracker = () => {
  const { role } = useDecodedToken();
  const { username } = useParams();
  
  // useRef persists across re-renders but resets on remount
  // localStorage persists across remounts (login causes remount)
  const hasProcessedCreatorLink = useRef<boolean>(false);

  const params = new URLSearchParams(window.location.search);
  const tllFromUrl = params.get("tll");

  const isAuthenticated = authService.isAuthenticated();
  const isAdminLink = !username;

  const { otherUser: user } = useFetchOtherUserInfos(username ?? null);

  const { item } = useSelector((state: StoreState) => state.users);

  let userId = authService.isAuthenticated() ? user?.id : item?._id;

  useEffect(() => {
    const tllFromStorage = sessionStorage.getItem("pendingTrackLink");
    const trackingLinkToken = tllFromUrl || tllFromStorage;
    
    if (!trackingLinkToken || (isAuthenticated && role === TYPE_CREATOR) || (username && !userId)) return;

    // CASE: Creator link - prevent double click
    if (!isAdminLink) {
      // Check 1: useRef prevents re-runs within same mount (dependency changes)
      if (hasProcessedCreatorLink.current) {
        hasProcessedCreatorLink.current = false;
        return;
      }
      
      // Check 2: localStorage prevents click after login (component remounts on login)
      const clickedBeforeLogin = localStorage.getItem("clickedBeforeLogin");
      if (isAuthenticated && clickedBeforeLogin === trackingLinkToken) {
        localStorage.removeItem("clickedBeforeLogin");
        hasProcessedCreatorLink.current = true;
        return;
      }
      
      // Mark: Set localStorage for unauthenticated (survives login remount)
      if (!isAuthenticated) {
        localStorage.setItem("clickedBeforeLogin", trackingLinkToken);
      }
      
      // Mark: Set ref to prevent re-runs within this mount
      hasProcessedCreatorLink.current = true;
    }

    const effectiveUserId = username ? userId : "admin";

    // CASE: Authenticated fan + Admin link (/?tll=123 -> /common/discover)
    // Prevent double-call during redirect
    if (isAuthenticated && isAdminLink) {
      const alreadyProcessed = sessionStorage.getItem("processedAdminLink");
      if (alreadyProcessed === trackingLinkToken) {
        // Already processed this token, clear and skip
        sessionStorage.removeItem("processedAdminLink");
        sessionStorage.removeItem("pendingTrackLink");
        return;
      }
      // Mark as processed BEFORE making the call
      sessionStorage.setItem("processedAdminLink", trackingLinkToken);
    }

    (async () => {
      // For unauthenticated users, check localStorage to prevent duplicate clicks
      const visitedProfileWithTrackingLink = trackingLinksService.getTable() || [];
      const isProfileVisited = visitedProfileWithTrackingLink.find((item: any) => item.creatorId === effectiveUserId);
      const isSameLink = isProfileVisited ? isProfileVisited?.trackingLinkToken === trackingLinkToken : false;

      if (!isAuthenticated) {
        if (isProfileVisited) {
          if (isSameLink) return;

          const data = [
            ...visitedProfileWithTrackingLink.filter((i: any) => i.creatorId !== effectiveUserId),
            {
              creatorId: effectiveUserId,
              trackingLinkToken,
            },
          ];

          trackingLinksService.saveTable(data);
        } else {
          const data = [
            ...visitedProfileWithTrackingLink,
            {
              creatorId: effectiveUserId,
              trackingLinkToken,
            },
          ];

          trackingLinksService.saveTable(data);
        }
      }

      /*
              UNAUTHENTICATED => Save to localStorage + count click (sync after login without counting)
              AUTHENTICATED => Count clicks
            */
      if (isAuthenticated) {
        // Authenticated: always count (creator link counts on reload too)
        await trackingLinksService.trackingLinkClicked(trackingLinkToken, username);
        // Clear pending track link after successful call
        sessionStorage.removeItem("pendingTrackLink");
      } else {
        // Unauthenticated: count only if not same link, save to localStorage for sync after login
        if ((isProfileVisited && !isSameLink) || !isProfileVisited) {
          await trackingLinksService.trackingLinkClicked(trackingLinkToken, username);
        }
      }
      removeTllFromUrl();
    })();
  }, [tllFromUrl, username, userId, role, isAdminLink, isAuthenticated]);

  return null;
};

export default TrackLinkTracker;
